import {Box} from '@indoqa/style-system'
import useEventListener from '@use-it/event-listener'
import {IStyle} from 'fela'
import * as React from 'react'
import {useFela} from 'react-fela'
import {useHistory} from 'react-router-dom'
import {authenticator} from '../../../app/auth/SessionPlusAuthenticator'
import {Theme} from '../../../app/theme'
import {Boundaries, Position, Resizer} from '../../../commons-shared/resizer/Resizer'
import {getKiaraUrl} from '../../../commons/config/getKiaraUrl'
import {useMobile} from '../../../commons/hooks/use-mobile/useMobile'
import {arPathBook} from '../../store/ar.paths'
import {useKiara} from './KiaraContext'

interface KiaraNavigateToBookMessage {
  source: 'kiara'
  type: 'navigateToBook'
  bookId: string
  chapterId?: string
  paraId?: string
  elementId?: string
}

interface KiaraReadyMessage {
  source: 'kiara'
  type: 'ready'
}

const KIARA_DEFAULT_WIDTH = 600
const KIARA_MIN_WIDTH = 300
const KIARA_MAX_WIDTH_RATIO = 0.8
const KIARA_WIDTH_KEY = 'oegbv/kiara-width'
const RESIZE_HANDLE_WIDTH = 8
const KIARA_ORIGIN = new URL(getKiaraUrl()).origin

// Kiara läuft cross-origin. Chrome setzt die Permissions Policy durch und gibt diese Features per
// Default nur an das eigene Dokument ('self') frei, deshalb müssen sie explizit an den Kiara-Origin
// delegiert werden. Ohne Origin-Angabe würde die Freigabe nur für den Embedder gelten und dem
// iframe nichts nützen.
const KIARA_FEATURES = ['clipboard-write', 'microphone', 'autoplay']
const KIARA_ALLOW = KIARA_FEATURES.map((feature) => `${feature} ${KIARA_ORIGIN}`).join('; ')

// The panel is mounted above the router so that its iframe survives navigation, while the page
// layout lives below it. The two sides are lined up through custom properties: ReaderLayout
// publishes the vertical band of its content row, the panel publishes the width it occupies.
const WIDTH_VAR = '--kiara-width'
const INSET_VAR = '--kiara-inset'
const TOP_VAR = '--reader-top'
const BOTTOM_VAR = '--reader-bottom'

const setCssVariable = (name: string, value: string) => {
  document.documentElement.style.setProperty(name, value)
}

const maxKiaraWidth = (): number => Math.max(KIARA_MIN_WIDTH, Math.round(window.innerWidth * KIARA_MAX_WIDTH_RATIO))

const clampKiaraWidth = (width: number): number => Math.min(Math.max(width, KIARA_MIN_WIDTH), maxKiaraWidth())

const getPersistentKiaraWidth = (): number => {
  const storedWidth = Number(window.localStorage.getItem(KIARA_WIDTH_KEY))
  const width = Number.isFinite(storedWidth) && storedWidth > 0 ? storedWidth : KIARA_DEFAULT_WIDTH
  return clampKiaraWidth(width)
}

const setPersistentKiaraWidth = (width: number) => {
  window.localStorage.setItem(KIARA_WIDTH_KEY, String(width))
}

// The session id is sensitive and is therefore NOT put into the iframe URL (where it would leak
// into logs, history and the Referer header). It is sent to the iframe via postMessage once the
// iframe announces it is ready (see the 'ready' handshake below).
const buildKiaraSrc = (): string => {
  const url = new URL(getKiaraUrl())
  url.searchParams.set('parentOrigin', window.location.origin)
  return url.toString()
}

const isOptionalString = (value: unknown): value is string | undefined =>
  value === undefined || typeof value === 'string'

const isKiaraNavigateToBookMessage = (data: unknown): data is KiaraNavigateToBookMessage => {
  const message = data as Partial<KiaraNavigateToBookMessage>
  return (
    typeof data === 'object' &&
    data !== null &&
    message.source === 'kiara' &&
    message.type === 'navigateToBook' &&
    typeof message.bookId === 'string' &&
    message.bookId.length > 0 &&
    isOptionalString(message.chapterId) &&
    isOptionalString(message.paraId) &&
    isOptionalString(message.elementId)
  )
}

const isKiaraReadyMessage = (data: unknown): data is KiaraReadyMessage => {
  const message = data as Partial<KiaraReadyMessage>
  return typeof data === 'object' && data !== null && message.source === 'kiara' && message.type === 'ready'
}

export const ArKiaraPanel = () => {
  const {css, theme} = useFela<Theme>()
  const history = useHistory()
  const {kiaraEnabled, showKiara, closeKiara} = useKiara()
  const mobile = useMobile()
  const iframeRef = React.useRef<HTMLIFrameElement>(null)
  const widthRef = React.useRef(getPersistentKiaraWidth())

  const kiaraSrc = React.useMemo(() => buildKiaraSrc(), [])
  const visible = kiaraEnabled && showKiara
  const visibleRef = React.useRef(visible)

  // Kiara is only offered on some pages, so it takes up no space anywhere else.
  React.useLayoutEffect(() => {
    visibleRef.current = visible
    setCssVariable(INSET_VAR, visible ? `${widthRef.current}px` : '0px')
    return () => setCssVariable(INSET_VAR, '0px')
  }, [visible])

  // Dragging updates the custom properties rather than React state: one imperative write moves both
  // the panel and the space the page reserves for it, without re-rendering the page on every frame.
  const applyWidth = React.useCallback((width: number) => {
    widthRef.current = width
    setCssVariable(WIDTH_VAR, `${width}px`)
    if (visibleRef.current) {
      setCssVariable(INSET_VAR, `${width}px`)
    }
  }, [])

  React.useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== KIARA_ORIGIN) {
        return
      }
      if (isKiaraReadyMessage(event.data)) {
        iframeRef.current?.contentWindow?.postMessage(
          {source: 'oegbv', type: 'session', sessionId: authenticator.sessionId()},
          KIARA_ORIGIN
        )
        return
      }
      if (isKiaraNavigateToBookMessage(event.data)) {
        const {bookId, chapterId, paraId, elementId} = event.data
        // On mobile Kiara covers the content, so it has to step aside for the chapter it points at.
        // On larger screens it sits beside the content and can stay open.
        if (mobile) {
          closeKiara()
        }
        history.push(arPathBook('', bookId, chapterId, paraId, undefined, undefined, elementId))
      }
    }
    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [history, closeKiara, mobile])

  // The resizer reports deltas of the mouse position: moving the left edge to the left widens the panel.
  const onResize = React.useCallback(
    (moveBy: number) => applyWidth(clampKiaraWidth(widthRef.current - moveBy)),
    [applyWidth]
  )

  const onResizeEnd = React.useCallback(() => setPersistentKiaraWidth(widthRef.current), [])

  // The panel is flush with the right window edge, so the mouse boundaries are the mirrored width boundaries.
  const getBoundaries = React.useCallback(
    (): Boundaries => ({
      min: window.innerWidth - maxKiaraWidth(),
      max: window.innerWidth - KIARA_MIN_WIDTH,
    }),
    []
  )

  // Re-read the stored width rather than the current one: a narrow viewport clamps the panel down,
  // and widening the window again has to restore the width the user actually chose.
  useEventListener('resize', () => applyWidth(getPersistentKiaraWidth()), undefined)

  // On mobile Kiara takes the full width below the header - there is no room for a docked column
  // and no resizer (the Resizer hides itself below the tablet breakpoint).
  const containerStyle: IStyle = {
    position: 'fixed',
    left: 0,
    right: 0,
    top: `var(${TOP_VAR}, 0px)`,
    bottom: `var(${BOTTOM_VAR}, 0px)`,
    zIndex: theme.zIndexes.absolute,
    display: visible ? 'block' : 'none',
    tablet: {
      left: 'auto',
      width: `var(${WIDTH_VAR}, ${widthRef.current}px)`,
      paddingLeft: RESIZE_HANDLE_WIDTH,
      borderLeftWidth: 1,
      borderLeftStyle: 'solid',
      borderLeftColor: theme.colors.border,
    },
    print: {
      display: 'none',
    },
  }
  const resizerStyle: IStyle = {
    width: RESIZE_HANDLE_WIDTH,
    backgroundColor: theme.colors.bgScrollbarTrack,
    ':hover': {
      backgroundColor: theme.colors.bgStructureHover,
    },
    '::after': {
      content: '" "',
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      width: 2,
      height: 24,
      borderLeftWidth: 1,
      borderLeftStyle: 'solid',
      borderLeftColor: theme.colors.bgScrollbarThumb,
      borderRightWidth: 1,
      borderRightStyle: 'solid',
      borderRightColor: theme.colors.bgScrollbarThumb,
    },
  }
  const iframeStyle: IStyle = {
    width: '100%',
    height: '100%',
    border: 0,
  }

  return (
    <Box style={containerStyle}>
      <Resizer
        pos={Position.LEFT}
        style={resizerStyle}
        onResize={onResize}
        onResizeEnd={onResizeEnd}
        getBoundaries={getBoundaries}
      />
      <iframe ref={iframeRef} title="Kiara" className={css(iframeStyle)} src={kiaraSrc} allow={KIARA_ALLOW}>
        Die Anzeige ist nicht möglich, da ihr Browser keine iFrames unterstützt.
      </iframe>
    </Box>
  )
}
